import React from "react";
import ReactDOM from "react-dom/client";
import App from "./App";
import "./index.css";

// Inject CSS variables inline to work around Exigo stripping variables from external stylesheets
const injectCssVariables = () => {
  const styleId = 'startsmart-css-vars';
  if (document.getElementById(styleId)) return;
  
  // Inject Google Fonts
  const fontLink = document.createElement('link');
  fontLink.rel = 'stylesheet';
  fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&family=Sora:wght@300;400;500;600;700;800&display=swap';
  document.head.appendChild(fontLink);
  
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = `
    #startsmart-scope,
    #startsmart-widget,
    #root,
    .idlife-bg {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
      
      /* IDLife Brand Colors */
      --idlife-blue: #0084FF;
      --idlife-blue-light: #33A3FF;
      --idlife-blue-dark: #0066CC;
      --idlife-purple: #6B46E5;
      --idlife-purple-light: #8B6EF2;
      --idlife-pink: #E94B9D;
      --idlife-teal: #00C9B7;
      --idlife-green: #00E5A0;
      
      /* Dark Mode Base */
      --app-bg: #0A0B0D;
      --app-fg: rgba(255, 255, 255, 0.96);
      --bg-gradient-start: #0A0B0D;
      --bg-gradient-mid: #12141A;
      --bg-gradient-end: #1A1D28;
      --bg-accent-blue: rgba(0, 132, 255, 0.08);
      --bg-accent-purple: rgba(107, 70, 229, 0.06);
      
      /* Surfaces */
      --surface-1: rgba(255, 255, 255, 0.04);
      --surface-2: rgba(255, 255, 255, 0.07);
      --surface-3: rgba(255, 255, 255, 0.10);
      --surface-glass: rgba(255, 255, 255, 0.03);
      
      /* Borders */
      --border-1: rgba(255, 255, 255, 0.08);
      --border-2: rgba(255, 255, 255, 0.12);
      --border-3: rgba(255, 255, 255, 0.16);
      --border-glow: rgba(0, 132, 255, 0.3);
      
      /* Typography */
      --text-primary: rgba(255, 255, 255, 0.96);
      --text-secondary: rgba(255, 255, 255, 0.72);
      --text-muted: rgba(255, 255, 255, 0.58);
      --text-subtle: rgba(255, 255, 255, 0.42);
      
      /* Interactive */
      --focus-ring: rgba(0, 132, 255, 0.45);
      --select-bg: rgba(0, 132, 255, 0.12);
      --select-border: rgba(0, 132, 255, 0.4);
      --select-glow: rgba(0, 132, 255, 0.6);
      --select-ring: rgba(0, 132, 255, 0.5);
      --btn-primary-from: #0084FF;
      --btn-primary-to: #0066CC;
      --btn-glow: rgba(0, 132, 255, 0.4);
      --radio: rgba(255, 255, 255, 0.16);
      --radio-selected: rgba(0, 132, 255, 1);
      
      /* Shadows */
      --shadow-sm: 0 2px 8px rgba(0, 0, 0, 0.08);
      --shadow-md: 0 8px 24px rgba(0, 0, 0, 0.12);
      --shadow-lg: 0 16px 48px rgba(0, 0, 0, 0.16);
      --shadow-xl: 0 24px 64px rgba(0, 0, 0, 0.24);
      --shadow-glow: 0 0 32px rgba(0, 132, 255, 0.35);
      --noise-opacity: 0.02;
    }
    
    @media (prefers-color-scheme: light) {
      #startsmart-scope,
      #startsmart-widget,
      #root,
      .idlife-bg {
        --app-bg: #FAFBFC;
        --app-fg: rgba(10, 11, 13, 0.92);
        --bg-gradient-start: #FFFFFF;
        --bg-gradient-mid: #F5F7FA;
        --bg-gradient-end: #EDF1F7;
        --bg-accent-blue: rgba(0, 132, 255, 0.05);
        --bg-accent-purple: rgba(107, 70, 229, 0.04);
        --surface-1: rgba(255, 255, 255, 0.90);
        --surface-2: rgba(255, 255, 255, 0.95);
        --surface-3: rgba(255, 255, 255, 1.0);
        --surface-glass: rgba(255, 255, 255, 0.70);
        --border-1: rgba(10, 11, 13, 0.08);
        --border-2: rgba(10, 11, 13, 0.12);
        --border-3: rgba(10, 11, 13, 0.16);
        --border-glow: rgba(0, 132, 255, 0.25);
        --text-primary: rgba(10, 11, 13, 0.92);
        --text-secondary: rgba(10, 11, 13, 0.72);
        --text-muted: rgba(10, 11, 13, 0.58);
        --text-subtle: rgba(10, 11, 13, 0.42);
        --focus-ring: rgba(0, 132, 255, 0.35);
        --select-bg: rgba(0, 132, 255, 0.08);
        --select-border: rgba(0, 132, 255, 0.3);
        --select-glow: rgba(0, 132, 255, 0.4);
        --select-ring: rgba(0, 132, 255, 0.4);
        --radio: rgba(10, 11, 13, 0.16);
        --radio-selected: rgba(0, 132, 255, 1);
        --shadow-sm: 0 2px 8px rgba(0, 0, 0, 0.04);
        --shadow-md: 0 8px 24px rgba(0, 0, 0, 0.06);
        --shadow-lg: 0 16px 48px rgba(0, 0, 0, 0.08);
        --shadow-xl: 0 24px 64px rgba(0, 0, 0, 0.12);
        --shadow-glow: 0 0 32px rgba(0, 132, 255, 0.2);
        --noise-opacity: 0.015;
      }
    }
    
    /* Force typography colors to override Exigo styles */
    #startsmart-scope h1,
    #startsmart-scope h2,
    #startsmart-scope h3,
    #startsmart-scope h4,
    #startsmart-scope p,
    #startsmart-scope span,
    #startsmart-scope label,
    #startsmart-scope div {
      color: var(--text-primary) !important;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
    }
    /* Force StartSmart button text to be white - higher specificity to override above */
    #startsmart-scope button.ss-start-btn,
    #startsmart-scope button.ss-start-btn span,
    #startsmart-scope button.ss-start-btn span.relative,
    #startsmart-scope button.ss-start-btn div {
      color: #FFFFFF !important;
    }
    #startsmart-scope button.ss-start-btn svg {
      stroke: #FFFFFF !important;
    }
    #startsmart-scope .text-\\[var\\(--text-secondary\\)\\] {
      color: var(--text-secondary) !important;
    }
    #startsmart-scope .text-\\[var\\(--text-muted\\)\\] {
      color: var(--text-muted) !important;
    }
    #startsmart-scope .text-\\[var\\(--text-subtle\\)\\] {
      color: var(--text-subtle) !important;
    }
    #startsmart-scope .text-white {
      color: white !important;
    }
    #startsmart-scope .text-\\[var\\(--app-fg\\)\\] {
      color: var(--app-fg) !important;
    }
    
    /* Force selected button shadow/glow effect */
    #startsmart-scope button[class*="select-bg"] {
      box-shadow: 0 0 0 2px rgba(0, 132, 255, 0.6), 0 18px 55px -30px rgba(0, 132, 255, 0.6) !important;
    }
  `;
  document.head.appendChild(style);
};

injectCssVariables();

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
